import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { useTranslation, Trans } from "react-i18next"
import { Modal } from "ui/Modal"
import { Text, Title } from "ui/Typography"
import { Flex } from "antd"
import WarningOutlinedWithBg from "ui/WarningOutlinedWithBg"
import PropTypes from "prop-types"
import UpcomingBookingListing from "./UpcomingBookingListing"
import AccountDeletionOptions from "../../AccountDeletionOptions"
import { spacings } from "themes/variables"
import ActionButtons from "../ActionButtons"
import { getGuideBookingsV2 } from "store/bookings"
import moment from "moment"

const ScheduleDeletionModal = ({ selected, setSelected, visible, onCancel, onProceed, primaryColor }) => {
  const dispatch = useDispatch()
  const [visibleCount, setVisibleCount] = useState(10)
  const [bookings, setBookings] = useState([])
  const { t } = useTranslation()
  const { last_booking_date } = useSelector((state) => state.user?.guide)
  const {
    items: bookingItems = [],
    metadata = {},
    loading = false
  } = useSelector(
    (state) => ({
      items: state.bookings?.items,
      metadata: state.bookings?.metadata,
      loading: state.bookings?.loading
    }),
    shallowEqual
  )

  const { currentPage, totalPages, totalResults, nextPage, prevPage, statusCounts } = useMemo(
    () => ({
      currentPage: metadata?.page || metadata?.current_page || 1,
      totalPages: metadata?.total_pages || 1,
      totalResults: metadata?.total_results,
      nextPage: metadata?.next_page,
      prevPage: metadata?.prev_page,
      statusCounts: metadata?.status_counts || {}
    }),
    [metadata]
  )

  const fetchedRef = useRef(false)

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    setVisibleCount(10)
    const params = {
      sort_by: "booked_date",
      order: "desc",
      type: "booked",
      page: 1,
      per_page: 10
    }
    const nextPage = null
    dispatch(getGuideBookingsV2(params, nextPage))
  }, [dispatch])

  useEffect(() => {
    if (bookingItems?.length) {
      setBookings((prev) => {
        if (currentPage === 1) return bookingItems
        const existingIds = new Set(prev.map((b) => b.id))
        const newBookings = bookingItems.filter((b) => !existingIds.has(b.id))
        return [...prev, ...newBookings]
      })
    }
  }, [bookingItems, currentPage])

  const handleLoadMore = useCallback(() => {
    if (currentPage < totalPages) {
      const params = {
        sort_by: "booked_date",
        order: "desc",
        type: "booked",
        page: currentPage + 1,
        per_page: 10
      }
      dispatch(getGuideBookingsV2(params, currentPage + 1))
    }
    setVisibleCount((count) => count + 10)
  }, [currentPage, totalPages, dispatch])

  const visibleBookings = useMemo(() => bookings.slice(0, visibleCount), [bookings, visibleCount])

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      centered
      width={700}
      title={
        <Flex justify="center">
          <WarningOutlinedWithBg />
        </Flex>
      }
    >
      <Flex vertical gap={spacings.XL}>
        <Flex vertical gap={spacings.M}>
          <Title level={3}>{t("settings.delete_account")}</Title>
          <Text>
            <Trans
              i18nKey="settings.account_deletion_description"
              values={{ date: last_booking_date ? moment(last_booking_date)?.format("MMMM D, YYYY") : "" }}
              components={{ strong: <strong /> }}
            />
          </Text>
        </Flex>
        <UpcomingBookingListing
          bookings={visibleBookings}
          onLoadMore={handleLoadMore}
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          nextPage={nextPage}
          prevPage={prevPage}
          statusCounts={statusCounts}
          loading={loading}
        />
        <Flex vertical gap={spacings.L}>
          <Title level={4}>{t("settings.account_deletion_options")}</Title>
          <AccountDeletionOptions setSelected={setSelected} selected={selected} />
        </Flex>
        <ActionButtons
          primaryColor={primaryColor}
          onSecondary={onCancel}
          onPrimary={onProceed}
          primaryLabel={t("settings.schedule_deletion")}
        />
      </Flex>
    </Modal>
  )
}

ScheduleDeletionModal.propTypes = {
  selected: PropTypes.string,
  setSelected: PropTypes.func,
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onProceed: PropTypes.func,
  primaryColor: PropTypes.string
}

export default ScheduleDeletionModal
